// frontend/src/components/Profile/AllDocumentsModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton,
  Box, Typography, Pagination, Stack, CircularProgress,
  Alert, alpha, useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Folder as FolderIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DocIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const API_BASE = 'https://fincollab.ru/api/auth';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(3),
    maxWidth: '90vw',
    width: 'auto',
    minWidth: 400,
    maxHeight: '90vh',
    backgroundColor: theme.palette.background.paper
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const DocumentsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  padding: theme.spacing(3),
  justifyContent: 'flex-start',
  minHeight: 400,
  position: 'relative'
}));

const DocumentCard = styled(Box)({
  width: 130,
  height: 130,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out',
  padding: 8,
  backgroundColor: 'transparent',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
  '&:hover .delete-button': {
    opacity: 1
  }
});

const DeleteButton = styled(IconButton)({
  position: 'absolute',
  top: 0,
  right: 0,
  opacity: 0,
  transition: 'opacity 0.2s',
  '&:hover': {
    color: '#d32f2f'
  }
});

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(3, 3, 4, 3),
  borderTop: `1px solid ${theme.palette.divider}`,
  marginTop: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  bottom: 0,
  zIndex: 10
}));

const LoaderContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 400,
  width: '100%'
});

const EmptyState = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 400,
  width: '100%'
});

const getFileIcon = (fileType) => {
  const type = (fileType || '').toLowerCase();
  if (type === 'pdf') return <PdfIcon sx={{ fontSize: 52, color: '#e74c3c' }} />;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(type))
    return <ImageIcon sx={{ fontSize: 52, color: '#3498db' }} />;
  if (['doc', 'docx'].includes(type)) return <DocIcon sx={{ fontSize: 52, color: '#2980b9' }} />;
  return <FileIcon sx={{ fontSize: 52, color: '#7f8c8d' }} />;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ITEMS_PER_PAGE = 20;

const AllDocumentsModal = ({
  open,
  onClose,
  documents = [],
  isOwnProfile,
  userId,
  onDocumentUpdate
}) => {
  const theme = useTheme();
  const [allDocuments, setAllDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const fetchAllDocuments = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const url = isOwnProfile
        ? `${API_BASE}/my-documents/`
        : `${API_BASE}/users/${userId}/documents/`;

      const response = await fetch(`${url}?page=${page}&page_size=${ITEMS_PER_PAGE}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAllDocuments(data.results || data);
        setTotalCount(data.count || (data.results || data).length);
      } else {
        setError('Не удалось загрузить документы');
      }
    } catch (err) {
      console.error('❌ Ошибка загрузки всех документов:', err);
      setError('Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setCurrentPage(1);
      if (documents.length > 0) {
        setAllDocuments(documents);
        setTotalCount(documents.length);
      } else {
        fetchAllDocuments(1);
      }
    }
  }, [open]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    if (documents.length === 0) {
      fetchAllDocuments(page);
    }
    const contentElement = document.querySelector('.MuiDialogContent-root');
    if (contentElement) {
      contentElement.scrollTop = 0;
    }
  };

  const handleDocumentClick = (doc) => {
    if (doc.file_url) {
      window.open(doc.file_url, '_blank');
    }
  };

  const handleDelete = async (docId, event) => {
    event.stopPropagation();

    if (!window.confirm('Удалить этот документ?')) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/my-documents/${docId}/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const updated = allDocuments.filter(d => d.id !== docId);
        setAllDocuments(updated);
        setTotalCount(prev => prev - 1);
        if (onDocumentUpdate) onDocumentUpdate();
      }
    } catch (err) {
      console.error('❌ Ошибка удаления документа:', err);
    }
  };

  const paginatedDocuments = documents.length > 0
    ? allDocuments.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
    : allDocuments;

  const displayTotalPages = documents.length > 0
    ? Math.ceil(allDocuments.length / ITEMS_PER_PAGE)
    : totalPages;

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth={false} fullWidth={false}>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FolderIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" component="div" fontWeight="700">
            Все документы
          </Typography>
          <Typography variant="body2" component="span" sx={{ color: 'white', opacity: 0.8 }}>
            ({totalCount})
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ m: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <DocumentsContainer>
          {loading ? (
            <LoaderContainer>
              <CircularProgress size={60} />
            </LoaderContainer>
          ) : paginatedDocuments.length === 0 ? (
            <EmptyState>
              <FolderIcon sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Нет документов
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Документы пока не загружены
              </Typography>
            </EmptyState>
          ) : (
            paginatedDocuments.map((doc) => (
              <DocumentCard key={doc.id} onClick={() => handleDocumentClick(doc)}>
                {isOwnProfile && (
                  <DeleteButton
                    className="delete-button"
                    size="small"
                    onClick={(e) => handleDelete(doc.id, e)}
                  >
                    <DeleteIcon fontSize="small" />
                  </DeleteButton>
                )}
                {getFileIcon(doc.file_type)}
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    fontSize: '0.65rem',
                    fontWeight: 300,
                    lineHeight: 1.2,
                    color: 'text.secondary',
                  }}
                >
                  {doc.title}
                </Typography>
                <Typography variant="caption" sx={{ mt: 0.25, fontSize: '0.6rem', fontWeight: 300, color: 'text.disabled' }}>
                  {doc.formatted_size || formatFileSize(doc.file_size)}
                </Typography>
              </DocumentCard>
            ))
          )}
        </DocumentsContainer>

        {displayTotalPages > 1 && (
          <PaginationContainer>
            <Stack spacing={2}>
              <Pagination
                count={displayTotalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
                disabled={loading}
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: theme.spacing(1),
                    fontSize: '1rem'
                  }
                }}
              />
            </Stack>
          </PaginationContainer>
        )}
      </DialogContent>
    </StyledDialog>
  );
};

export default AllDocumentsModal;